import { useEffect, useRef } from "react";

import { useBool } from "@/hooks/useBool";
import { useSidebarManager } from "@/hooks/useSidebar";
import { type RuntimeFloatingPanelAttributes, useRuntimeFloatingPanel } from "@/ui-adapters/RuntimeFloatingPanel";

interface UseSidebarCollapsedTooltipReturn {
  showTooltip: () => void;
  hideTooltip: () => void;
  referenceRef: React.RefCallback<HTMLElement>;
  panelProps: RuntimeFloatingPanelAttributes;
  isTooltipVisible: boolean;
  isCollapsed: boolean;
}

export function useSidebarCollapsedTooltip(): UseSidebarCollapsedTooltipReturn {
  const { isCollapsed } = useSidebarManager();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isTooltipVisible, tooltipVisibilityHandlers] = useBool(false);
  const { referenceRef, panelProps } = useRuntimeFloatingPanel({ open: isTooltipVisible, placement: "right" });

  const showTooltip = () => {
    if (!isCollapsed) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      tooltipVisibilityHandlers.setTrue();
    }, 300);
  };

  const hideTooltip = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    tooltipVisibilityHandlers.setFalse();
  };

  // Browser focus and pointer events can replace mouseleave, so keep the tooltip in sync with them.
  useEffect(() => {
    const dismissTooltip = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      tooltipVisibilityHandlers.setFalse();
    };

    window.addEventListener("blur", dismissTooltip);
    document.addEventListener("pointerdown", dismissTooltip);

    return () => {
      window.removeEventListener("blur", dismissTooltip);
      document.removeEventListener("pointerdown", dismissTooltip);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [tooltipVisibilityHandlers]);

  // Hide when sidebar expands — mouseleave won't fire if the element moves out from under the cursor
  useEffect(() => {
    if (!isCollapsed) {
      tooltipVisibilityHandlers.setFalse();
    }
  }, [isCollapsed, tooltipVisibilityHandlers]);

  return { showTooltip, hideTooltip, referenceRef, panelProps, isTooltipVisible, isCollapsed };
}
